import * as Sentry from '@sentry/browser';
import memoize from 'memoize-one';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import sortBy from 'sort-by';

import { trackCustomEvent } from 'my-core/gtm-events';
import { useCurrentUser, useStandardApiRequest } from 'my-core/hooks';
import buildQuizScoreResults from 'my-core/quiz-score-calculator';

import { fetchQuiz } from 'my-actions/QuizActions';
import {
  clearForQuiz,
  createQuizSubmissions,
  updateQuizSubmissionState,
} from 'my-actions/QuizQuestionSubmissionActions';

// `attemptContext` says where the quiz is being taken — a study session's diagnostic, a
// tutor's chat card — and is spread verbatim into the submission bodies so the shape stays
// the server's business. Optional, and absent everywhere a quiz is taken inside a course.
export default function QuizCtnr({
  attemptContext,
  children,
  courseAssignmentId,
  eventTrackingProperties,
  onlineCourseId,
  quizId,
  shareId,
  studyPlannerItemId,
}) {
  const currentUser = useCurrentUser();

  const fetchQuizKey = `fetch_quiz_ctnr_${quizId}_${onlineCourseId}_${courseAssignmentId}_${shareId}_${studyPlannerItemId}`;
  const { performRequest: performFetchQuiz, requestStatus: fetchQuizStatus } = useStandardApiRequest({
    actionCreator: fetchQuiz,
    requestKey: fetchQuizKey,
  });
  useEffect(() => {
    if (fetchQuizStatus) return;
    performFetchQuiz(
      quizId,
      {
        online_course_id: onlineCourseId,
        course_assignment_id: courseAssignmentId,
        study_planner_item_id: studyPlannerItemId,
        share_id: shareId,
        include: ['questions'],
      },
      fetchQuizKey,
    );
  }, [
    courseAssignmentId,
    fetchQuizKey,
    fetchQuizStatus,
    onlineCourseId,
    performFetchQuiz,
    quizId,
    shareId,
    studyPlannerItemId,
  ]);

  const quiz = useSelector(state => getQuiz(fetchQuizStatus, state.quizzes.items, state.quizQuestions.items));

  const [guestSubmissions, setGuestSubmissions] = useState([]);
  const submissions = useSelector(state =>
    getQuizSubmissions(quizId, currentUser.id, onlineCourseId, state.quizQuestionSubmissions.items),
  );

  const { performRequest: performSaveSubmissions, requestStatus: saveSubmissionsStatus } = useStandardApiRequest({
    actionCreator: createQuizSubmissions,
    onError: error => Sentry.captureMessage('QuizCtnr: saveSubmission error', { extra: { error } }),
  });

  const saveSubmissions = useCallback(
    (submissions, params) => {
      if (currentUser.guest) {
        const qqIds = submissions.map(s => s.quiz_question_id);
        setGuestSubmissions(gsubs => [
          ...gsubs.filter(s => !qqIds.includes(s.quiz_question_id)),
          ...submissions.map(({ quiz_question_id, submission }) => ({
            quiz_question_id,
            current_submission: submission,
            submission_attempts: { current: submission },
          })),
        ]);
      } else {
        performSaveSubmissions({
          quiz_id: quizId,
          online_course_id: onlineCourseId,
          submissions,
          ...attemptContext,
          ...params,
        });
      }
    },
    [currentUser.guest, onlineCourseId, performSaveSubmissions, quizId, attemptContext],
  );

  const { performRequest: performSubmitQuiz, requestStatus: submitQuizStatus } = useStandardApiRequest({
    actionCreator: createQuizSubmissions,
    onError: error => Sentry.captureMessage('QuizCtnr: submitQuiz error', { extra: { error } }),
  });
  const [guestSubmitStatus, setGuestSubmitStatus] = useState();
  // Resolves once the attempt is on the server, so a caller can wait for it before telling
  // anything else the quiz is finished (see CourseAssessment).
  const submitQuiz = useCallback(
    submissions => {
      if (currentUser.guest) {
        setGuestSubmissions(gsubs =>
          quiz.elements
            .filter(e => e.quiz_question_id)
            .map(e => {
              const submission =
                submissions.find(s => s.quiz_question_id === e.quiz_question_id)?.submission ||
                gsubs?.find(s => s.quiz_question_id === e.quiz_question_id);
              const finalSub = { response: null, result: { points: 0, score: 0, total: 1 }, ...submission };
              return {
                quiz_question_id: e.quiz_question_id,
                current_submission: finalSub,
                submission_attempts: { current: finalSub },
              };
            }),
        );
        setGuestSubmitStatus({ fetching: true });
        return new Promise(resolve =>
          setTimeout(() => {
            setGuestSubmitStatus({ success: true });
            trackQuizSubmitted(quiz, submissions, eventTrackingProperties);
            resolve();
          }, 500),
        );
      }
      return performSubmitQuiz({
        quiz_id: quizId,
        online_course_id: onlineCourseId,
        submissions,
        new_attempt: true,
        full_quiz_submit: true,
        ...attemptContext,
      }).then(err => {
        if (!err) trackQuizSubmitted(quiz, submissions, eventTrackingProperties);
        return err;
      });
    },
    [currentUser.guest, onlineCourseId, performSubmitQuiz, quizId, quiz, eventTrackingProperties, attemptContext],
  );

  // UPDATE QUIZ STATE
  const { performRequest: performUpdateQuizState /*, requestStatus: updateQuizStateStatus */ } = useStandardApiRequest({
    actionCreator: updateQuizSubmissionState,
  });
  const updateQuizState = useCallback(
    state => {
      if (!currentUser.guest) performUpdateQuizState(quizId, onlineCourseId, state);
    },
    [currentUser.guest, onlineCourseId, performUpdateQuizState, quizId],
  );

  // RESTART QUIZ
  const { performRequest: performRestartQuiz, requestStatus: restartQuizStatus } = useStandardApiRequest({
    actionCreator: clearForQuiz,
  });

  const restartQuiz = useCallback(() => {
    if (currentUser.guest) {
      setGuestSubmissions([]);
    } else {
      performRestartQuiz(quizId, onlineCourseId);
    }
  }, [currentUser.guest, onlineCourseId, performRestartQuiz, quizId]);

  return children({
    fetching: !fetchQuizStatus || fetchQuizStatus?.fetching,
    fetchStatus: fetchQuizStatus,
    quiz,
    submissions: currentUser.guest ? guestSubmissions : submissions,
    onRestartQuiz: restartQuiz,
    onSaveSubmissions: saveSubmissions,
    onSubmitQuiz: submitQuiz,
    onUpdateQuizState: updateQuizState,
    restartQuizStatus,
    saveSubmissionsStatus,
    submitQuizStatus: currentUser.guest ? guestSubmitStatus : submitQuizStatus,
  });
}

const getQuiz = memoize((status, quizzes, quizQuestions) => {
  if (!status?.success) return;
  const quiz = quizzes[status.id];
  return (
    quiz && {
      ...quiz,
      elements: quiz.elements
        .map(element => ({
          ...element,
          question: element.quiz_question_id && {
            ...quizQuestions[element.quiz_question_id],
            linked_oc_sections: status.question_linked_oc_sections?.[element.quiz_question_id],
          },
        }))
        .sort(sortBy('order')),
    }
  );
});
const getQuizSubmissions = memoize((quizId, userId, onlineCourseId, submissions) =>
  Object.values(submissions).filter(
    s => s.quiz_id === quizId && s.user_id === userId, // && (!onlineCourseId || s.online_course_id === onlineCourseId)
  ),
);

function trackQuizSubmitted(quiz, submissions, eventTrackingProperties) {
  const score = computeQuizScore(quiz, submissions);
  trackCustomEvent(
    'Quiz Submitted',
    {
      quiz_id: quiz.id,
      ...eventTrackingProperties,
      ...(score && {
        quiz_score_percent: score.percent,
        quiz_score_points: score.points,
        quiz_score_total: score.total,
      }),
    },
    { custIO: true },
  );
}

function computeQuizScore(quiz, submissions) {
  if (!quiz?.elements?.length || !submissions?.length) return null;
  const attemptSubmissions = submissions.map(({ quiz_question_id, submission }) => ({
    quiz_question_id,
    submission_attempts: { current: submission },
  }));
  const results = buildQuizScoreResults('current', quiz.elements, quiz.computed_scores, attemptSubmissions);
  if (!results.total) return null;
  return {
    points: Math.round(results.points * 100) / 100,
    total: results.total,
    percent: Math.round((results.points / results.total) * 1000) / 10,
  };
}
